import type { DisplayKind } from "../lib/stream-status";

const FRAME: React.CSSProperties = {
  border: "1px solid var(--ask-frame-border, var(--color-border-secondary))",
  borderRadius: "var(--ask-frame-radius, 12px)",
  background: "var(--ask-frame-bg, var(--color-bg-primary))",
  marginBottom: 14,
  overflow: "hidden",
};

function Bar({ w, h = 10, style }: { w: number | string; h?: number; style?: React.CSSProperties }) {
  return <span className="ask-skeleton" style={{ display: "block", width: w, height: h, ...style }} />;
}

function NumberSkeleton() {
  return (
    <div style={{ ...FRAME, padding: 20, display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 16 }}>
      {[0, 1, 2].map((i) => (
        <div key={i} style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <Bar w="60%" h={9} />
          <Bar w="80%" h={20} />
          <Bar w={56} h={14} style={{ borderRadius: 999 }} />
        </div>
      ))}
    </div>
  );
}

function ListSkeleton() {
  return (
    <div style={FRAME}>
      {[0, 1, 2].map((i) => (
        <div
          key={i}
          style={{
            display: "grid",
            gridTemplateColumns: "18px 30px minmax(0, 1fr) 64px",
            gap: 12,
            alignItems: "center",
            padding: "10px 16px",
            borderTop: i ? "1px solid var(--color-border-secondary)" : undefined,
          }}
        >
          <Bar w={12} />
          <Bar w={30} h={42} />
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <Bar w="55%" h={11} />
            <Bar w="35%" h={9} />
          </div>
          <Bar w="100%" h={11} />
        </div>
      ))}
    </div>
  );
}

function VideosSkeleton() {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))",
        gap: 12,
        marginBottom: 14,
      }}
    >
      {[0, 1, 2].map((i) => (
        <div key={i} style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <span
            className="ask-skeleton"
            style={{ display: "block", width: "100%", aspectRatio: "9 / 14", borderRadius: 12 }}
          />
          <Bar w="60%" h={10} />
        </div>
      ))}
    </div>
  );
}

function TableSkeleton() {
  return (
    <div style={FRAME}>
      <div style={{ padding: "11px 16px", background: "var(--color-bg-secondary)", display: "flex", gap: 24 }}>
        {[90, 60, 60, 60].map((w, i) => (
          <Bar key={i} w={w} h={9} />
        ))}
      </div>
      {[0, 1, 2, 3].map((r) => (
        <div
          key={r}
          style={{
            padding: "11px 16px",
            borderTop: "1px solid var(--color-border-secondary)",
            display: "flex",
            gap: 24,
          }}
        >
          {[120, 60, 60, 60].map((w, i) => (
            <Bar key={i} w={w} h={10} />
          ))}
        </div>
      ))}
    </div>
  );
}

function ChartSkeleton() {
  return (
    <div style={{ ...FRAME, padding: 20 }}>
      <Bar w="40%" h={12} style={{ marginBottom: 16 }} />
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {[92, 74, 58, 44, 30].map((w, i) => (
          <div
            key={i}
            style={{ display: "grid", gridTemplateColumns: "80px minmax(0, 1fr)", gap: 10, alignItems: "center" }}
          >
            <Bar w="80%" h={9} />
            <Bar w={`${w}%`} h={16} />
          </div>
        ))}
      </div>
    </div>
  );
}

/** Holds the display's space, in its shape, until the display itself streams in. */
export function DisplaySkeleton({ kind }: { kind: DisplayKind }) {
  const label = `Loading ${kind === "number" ? "headline numbers" : kind}`;
  const body = {
    number: <NumberSkeleton />,
    list: <ListSkeleton />,
    videos: <VideosSkeleton />,
    table: <TableSkeleton />,
    chart: <ChartSkeleton />,
  }[kind];
  return (
    <div aria-busy="true" aria-label={label}>
      {body}
    </div>
  );
}
